import { Body, Callout, TextLink, Tooltip } from '@assembly-js/design-system';
import { Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import { FormikProps, FormikValues } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import {
  ModuleTypeValue,
  URLValidationRegex,
  VisibilityRule,
} from 'src/constants';
import { docsURL } from 'src/constants/externalUrls';
import {
  AppPresets,
  renameDisabledModules,
} from 'src/constants/moduleManagementConsts';
import { RouteContext } from 'src/context';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import { useExtensionFormFieldsHandlers } from 'src/hooks/useExtensionFormFieldsHandlers';
import { AppVisibilityConfigSetting } from 'src/legacy/components/app-visibility/AppVisibilityConfigSetting';
import { RemoveApp } from 'src/legacy/components/AppSetup/RemoveApp';
import { ExtensionEmbedFieldLabel } from 'src/legacy/components/ExtensionPage/ExtensionEmbedFieldLabel';
import { ExtensionModuleContentPreview } from 'src/legacy/components/Settings/AppSetupPage/ExtensionModuleContentPreview/ExtensionModuleContentPreview';
import { ModuleTypes } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/appSetupTypes';
import { URLQueryParamsSection } from 'src/legacy/components/Settings/AppSetupPage/SetupApp/URLQueryParamsSection';
import {
  ExtensionViewType,
  ExtensionViewTypeValue,
} from 'src/legacy/components/Settings/ExtensionsPage/ExtensionViewType';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import {
  BaseTextField,
  ResizableTextField,
} from 'src/legacy/components/TextField';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';
import * as Colors from 'src/theme/colors';
import { VisibilityScenario } from 'src/utils/app_visibility';
import { createEmbedBody } from 'src/utils/ExtensionUtils';

const useStyles = makeStyles()((theme) => ({
  instructionTextWrapper: {
    marginBottom: theme.spacing(3),
  },
  iconPickerWrapper: {
    marginTop: theme.spacing(3.5),
  },
  extensionTypeSelectWrapper: {
    marginTop: theme.spacing(2.5),
  },
  embedInputWrapper: {
    marginTop: theme.spacing(4),
  },
  flexContainer: {
    display: 'flex',
  },
  helperText: {
    color: Colors.GraySmall,
  },
  extensionViewTypeWrapper: {
    marginTop: theme.spacing(3.5),
  },
  flexAlignCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  removeApp: {
    paddingTop: theme.spacing(3.5),
    marginBottom: 100,
  },
  switchContainer: {
    alignItems: 'center',
    borderWidth: '1px 0px',
    borderStyle: 'solid',
    borderColor: theme.palette.divider,
    display: 'flex',
    flexDirection: 'row',
    marginTop: theme.spacing(3),
    padding: theme.spacing(3, 0),
  },
  switchText: {
    flex: 1,
  },
}));

function renderAppVisibilityConfig(
  isAddingNewApp: boolean,
  setFieldValue: (field: string, value: VisibilityRule[]) => void,
) {
  if (isAddingNewApp) {
    return (
      <AppVisibilityConfigSetting
        scenario={VisibilityScenario.AddAnApp}
        onVisibilityConfigSaved={(visibilityRules) => {
          setFieldValue('visibilityConfig', visibilityRules);
        }}
      />
    );
  }

  return <AppVisibilityConfigSetting scenario={VisibilityScenario.EditApp} />;
}

/** Returns true when the URL query-params section should be shown for a module type. */
export function isQueryParamsVisible(type: string): boolean {
  return (
    type === ModuleTypeValue.GLOBAL_EXTENSION ||
    type === ModuleTypes.Authenticated
  );
}

export const commonValidationSchemaItems = {
  id: Yup.string(), // used only to check if the module if rename-able
  label: Yup.string().trim().required('Name is required'),
  icon: Yup.string().required('App icon is required'),
};

export function useEditModuleForm<Values extends FormikValues>() {
  const { t } = useTranslation();
  const { query } = React.useContext(RouteContext);
  const { moduleId, moduleType, preset = '', appId: newAppId } = query;
  const { data: { extensionsSettings } = {} } = useGetInstallsQuery();
  const coreSchemaItems = {
    ...commonValidationSchemaItems,
    type: Yup.string(),
    autoSizeEmbed: Yup.boolean().when('type', {
      is: 'global',
      then: Yup.boolean().required(),
    }),
    content: Yup.string()
      .trim()
      .nullable()
      .when('type', {
        is: (type) => type !== ModuleTypes.Local,
        then: Yup.string().required('Invalid embed content'),
      }),
    embedCode: Yup.string()
      .trim()
      .nullable()
      // when editing a non-local extension with embed view type, embedCode is required
      .when(['type', 'viewType'], {
        is: (type, viewType) =>
          type !== ModuleTypes.Local &&
          viewType === ExtensionViewTypeValue.EMBED,
        then: Yup.string().required('Embed code cannot be empty'),
      })
      .when('type', {
        is: (type) => type === ModuleTypes.Authenticated,
        then: Yup.string()
          .required('Authenticated extension requires a url')
          .matches(URLValidationRegex, 'Invalid embed code URL.'),
      }),
    // when view type is link, embedLink is required
    embedLink: Yup.string()
      .trim()
      .nullable()
      // when editing a non-local extension with embed view type, embedLink is required
      .when(['type', 'viewType'], {
        is: (type, viewType) =>
          type !== ModuleTypes.Local &&
          viewType === ExtensionViewTypeValue.LINK,
        then: Yup.string()
          .required('Embed link cannot be empty')
          .matches(URLValidationRegex, 'Invalid embed link URL.'),
      }),
    viewType: Yup.string().required('View type is required'),
    queryParams: Yup.array()
      .nullable()
      .of(
        Yup.object().shape({
          key: Yup.string().required('Key is required'),
          value: Yup.string().required('Value is required'),
        }),
      ),
  };
  const marketplaceSchemaItems = {
    ...commonValidationSchemaItems,
  };

  // appId can be passed in via the URL ?appId= for new installs or, if
  // the app is already isntalled, we grab it from extension settings.
  const appId = newAppId ?? extensionsSettings?.[moduleId]?.appId;
  const isMarketplaceApp = !!appId;
  const { classes } = useStyles();

  const FormRenderer: React.FC<FormikProps<Values>> = ({
    values,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    touched,
  }) => {
    const {
      handleEmbedContentInputChange,
      handleViewTypeChanged,
      contentFieldHelperText,
    } = useExtensionFormFieldsHandlers(
      setFieldValue,
      handleChange,
      values,
      errors,
      touched,
    );

    const renderModuleNameInput = (options?: { disabled: boolean }) => (
      <BaseTextField
        autoFocus
        fullWidth
        label={
          <div className={classes.flexAlignCenter}>
            <BaseTypography fontType="13Medium">Name</BaseTypography>
            <BaseTypography className={classes.helperText}>
              &nbsp;- How it should appear in the sidebar
            </BaseTypography>
          </div>
        }
        variant="outlined"
        sizeVariant="medium"
        name="label"
        value={values.label}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched && touched.label && errors && errors.label)}
        // undefined (not ' ') so the field collapses the helper-text line when there's no error, avoiding reserved empty space.
        helperText={
          touched?.label && typeof errors?.label === 'string'
            ? errors.label
            : undefined
        }
        {...(options || {})}
      />
    );

    const visibility = {
      customAppHelp: values.type === ModuleTypes.Authenticated,
      content:
        (values.type === ModuleTypes.Global ||
          values.type === ModuleTypes.Authenticated) &&
        !isMarketplaceApp,
      distribution:
        moduleType === 'extension' &&
        preset !== AppPresets.CustomApp &&
        !isMarketplaceApp,
      embedSizing:
        values.viewType === ExtensionViewTypeValue.EMBED && !isMarketplaceApp,
      preview:
        values.viewType === ExtensionViewTypeValue.EMBED &&
        values.type !== ModuleTypes.Authenticated &&
        !isMarketplaceApp,
      viewType:
        values.type === ModuleTypeValue.GLOBAL_EXTENSION && !isMarketplaceApp,
      queryParams: isQueryParamsVisible(values.type),
    };

    const isAddingNewApp = moduleId === 'new';

    return (
      <>
        {renameDisabledModules.includes(values.id) ? (
          <Tooltip
            position="bottom"
            content={
              <Body size="sm">
                Editing the name is disabled for this module.
              </Body>
            }
          >
            <div>{renderModuleNameInput({ disabled: true })}</div>
          </Tooltip>
        ) : (
          renderModuleNameInput()
        )}
        <div className={classes.iconPickerWrapper}>
          <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
          <SuggestIconPicker
            value={values.icon}
            onChange={(iconName) => setFieldValue('icon', iconName)}
            appName={values.label}
          />
        </div>
        {visibility.distribution && (
          <div className={classes.extensionTypeSelectWrapper}>
            <BaseTextField
              sizeVariant="medium"
              select
              fullWidth
              variant="outlined"
              onChange={handleChange}
              label="Setup type"
              onBlur={handleBlur}
              name="type"
              defaultValue="global"
              value={values.type}
              disabled={Boolean(values.id)} // disabled when editing an existing module
            >
              <MenuItem value="global">
                {t(($) => $.page_apps.app_type_descriptions.automatic)}
              </MenuItem>
              <MenuItem value="local">
                {t(($) => $.page_apps.app_type_descriptions.manual)}
              </MenuItem>

              {/* preset custom extensions should cannot be authenticated since they are not customizable by customers */}
              {!preset && (
                <MenuItem value="authenticated">
                  {t(($) => $.page_apps.app_type_descriptions.custom)}
                </MenuItem>
              )}
            </BaseTextField>
            {values.type === ModuleTypeValue.LOCAL_EXTENSION && (
              <div className="mt-8">
                <Callout
                  description={t(
                    ($) => $.page_apps.manual_content_connection_description,
                  )}
                />
              </div>
            )}
          </div>
        )}
        {visibility.viewType && (
          <div className={classes.extensionViewTypeWrapper}>
            <BaseTypography fontType="13Medium">View type</BaseTypography>
            <ExtensionViewType
              onChange={handleViewTypeChanged}
              onBlur={handleBlur}
              value={values.viewType}
              fieldName="viewType"
            />
          </div>
        )}
        {visibility.content && (
          <>
            <div className={classes.embedInputWrapper}>
              <ResizableTextField
                fullWidth
                error={Boolean(contentFieldHelperText())}
                helperText={contentFieldHelperText() || ' '}
                variant="outlined"
                name="content"
                onChange={handleEmbedContentInputChange}
                value={values.content}
                onBlur={handleBlur}
                label={
                  values.type === ModuleTypes.Authenticated ? (
                    <BaseTypography fontType="13Medium">
                      Custom App URL
                    </BaseTypography>
                  ) : (
                    <ExtensionEmbedFieldLabel
                      extensionViewType={values.viewType}
                    />
                  )
                }
              />
            </div>

            {/* Render render auto-size and preview when extension view type is embed */}
            {visibility.embedSizing && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.autoSizeEmbed}
                    onChange={handleChange}
                    name="autoSizeEmbed"
                    color="primary"
                  />
                }
                label="Auto size embed"
                id="autoSize-input"
              />
            )}

            {/* Render extension preview when view type is embed and it is an not authenticated extension */}
            {visibility.preview && (
              <ExtensionModuleContentPreview
                extensionIcon={values.icon}
                extensionName={values.label}
                embedContent={
                  values.embedlyData?.html || values.embedCode
                    ? createEmbedBody({
                        embedCode: values.embedlyData?.html || values.embedCode,
                        autoSize: values.autoSizeEmbed,
                      })
                    : ''
                }
              />
            )}

            {visibility.customAppHelp && (
              <div className="mt-8">
                <Callout
                  description={
                    <>
                      {t(
                        ($) =>
                          $.page_apps.app_type_descriptions.custom_description,
                      )}{' '}
                      You can learn more about custom apps{' '}
                      <TextLink
                        className="inline"
                        href={`${docsURL}/page/custom-apps`}
                        target="_blank"
                      >
                        here.
                      </TextLink>
                    </>
                  }
                />
              </div>
            )}

            {visibility.queryParams && (
              <URLQueryParamsSection
                queryParams={values.queryParams}
                content={values.content}
                setFieldValue={setFieldValue}
              />
            )}
          </>
        )}
        {renderAppVisibilityConfig(isAddingNewApp, setFieldValue)}
        <div className={classes.removeApp}>
          <RemoveApp />
        </div>
      </>
    );
  };

  const hasCoreSchema =
    ['global', 'local', 'extension'].includes(moduleType) && !isMarketplaceApp;
  const marketplaceValidationScheme = Yup.object().shape(
    marketplaceSchemaItems,
  );
  const coreValidationScheme = Yup.object().shape(coreSchemaItems);

  return {
    FormRenderer,
    validationScheme: hasCoreSchema
      ? coreValidationScheme
      : marketplaceValidationScheme,
  };
}
